import clsx from 'clsx';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlateElement, useEditorRef } from 'platejs/react';
import { useComboboxInput, useHTMLInputCursorState } from '@platejs/combobox/react';
import { usePlateEditorConfig } from '../../PlateEditorConfigContext';
import type { MentionUser } from '../../types/mention';
import styles from './MentionInputElement.module.scss';
import { MENTION_NODE_TYPE, MENTION_PREFIX, MENTION_NO_RESULTS_TEXT } from './constants';
import { computeMentionDropdownPosition, type MentionDropdownPosition } from './mentionDropdownPosition';
import { insertMentionForUser } from './insertMention';

// The @-combobox. @platejs/mention inserts a `mention_input` void inline node
// when the user types `@`; this component renders for that node — a tiny
// inline text input (the live query) plus a floating candidate list read from
// PlateEditorConfigContext (`mentionableUsers`).
//
// We don't pull in a heavyweight combobox UI library (ariakit/cmdk aren't in
// the dependency tree). Instead we drive the two low-level primitives the
// editor ships with:
//   - useComboboxInput: owns blur/escape/backspace cancellation and removing
//     the mention_input node (cancelInput) so the `@` text doesn't linger.
//   - useHTMLInputCursorState: feeds cursor position so backspace-at-start /
//     arrow-out cancel correctly.
//
// On select we call the plugin's `insert.mention` transform — that replaces
// the mention_input node with `{ type:'mention', key:userId, value:'@Name',
// children:[{text:''}] }` and (because the plugin is configured with
// insertSpaceAfterMention) appends a trailing space.

function filterUsers(users: MentionUser[], query: string): MentionUser[] {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
        const name = (u.name ?? '').toLowerCase();
        const email = (u.emailId ?? '').toLowerCase();
        return name.includes(q) || email.includes(q);
    });
}

export function MentionInputElement(props: any) {
    const editor = useEditorRef();
    const { mentionableUsers, renderedInBottomSheet } = usePlateEditorConfig();

    const inputRef = useRef<HTMLInputElement>(null);
    const wrapRef = useRef<HTMLSpanElement>(null);
    const dropdownRef = useRef<HTMLSpanElement>(null);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [pos, setPos] = useState<MentionDropdownPosition | null>(null);

    const cursorState = useHTMLInputCursorState(inputRef);
    const { props: inputProps, removeInput } = useComboboxInput({
        ref: inputRef,
        cursorState,
        cancelInputOnBlur: !renderedInBottomSheet,
    });

    const filtered = useMemo(() => filterUsers(mentionableUsers ?? [], query), [mentionableUsers, query]);

    // The candidate list is portaled to document.body so it escapes the comment
    // box's `overflow: hidden` (and the narrow drawer). Recompute its fixed
    // position from the inline input's rect whenever the query/result count
    // changes, and on scroll/resize so it tracks the caret.
    useLayoutEffect(() => {
        const update = () => {
            const el = wrapRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            setPos(
                computeMentionDropdownPosition(
                    { left: r.left, top: r.top, bottom: r.bottom },
                    { width: window.innerWidth, height: window.innerHeight },
                ),
            );
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [query, filtered.length]);

    // When cancelInputOnBlur is disabled (portal case), dismiss the combobox
    // on mousedown outside both the inline input and the portaled dropdown.
    useEffect(() => {
        if (!renderedInBottomSheet) return;
        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (wrapRef.current?.contains(target)) return;
            if (dropdownRef.current?.contains(target)) return;
            removeInput(true);
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [renderedInBottomSheet, removeInput]);

    const selectUser = (user: MentionUser) => {
        // Focus/selection currently lives in the HTML combobox input — hand it
        // back to Slate, then insert via the canonical handler so the caret
        // lands AFTER the chip + trailing space (RECUR-26761 AC4), not before it.
        removeInput(true);
        try {
            insertMentionForUser(editor, user, query);
        } catch {
            /* defensive: mention plugin not ready */
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Let useComboboxInput handle escape/backspace/arrow-out cancellation.
        inputProps.onKeyDown(e);
        if (e.defaultPrevented) return;
        if (!filtered.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % filtered.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            const user = filtered[Math.min(activeIndex, filtered.length - 1)];
            if (user) selectUser(user);
        }
    };

    return (
        <PlateElement {...props} as="span" attributes={{ ...props.attributes }}>
            <span ref={wrapRef} contentEditable={false} className={styles.Wrap}>
                {/* The `@` trigger is consumed into the void mention_input node,
                    so render it explicitly — otherwise the field looks empty
                    until a letter is typed (RECUR-26761 AC3). */}
                <span aria-hidden className={styles.Trigger}>
                    {MENTION_PREFIX}
                </span>
                <input
                    ref={inputRef}
                    className={styles.Input}
                    value={query}
                    aria-label="Mention search"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActiveIndex(0);
                    }}
                    onKeyDown={onKeyDown}
                    onBlur={inputProps.onBlur}
                />
            </span>
            {createPortal(
                <span
                    ref={dropdownRef}
                    className={styles.Dropdown}
                    role="listbox"
                    data-mention-type={MENTION_NODE_TYPE}
                    // Only `position`/coords are inline — these are measured at
                    // runtime from the caret and can't live in SCSS (see Rule 12).
                    style={
                        pos
                            ? { position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom }
                            : { position: 'fixed', visibility: 'hidden' }
                    }
                >
                    {filtered.length === 0 ? (
                        <span className={styles.Empty}>{MENTION_NO_RESULTS_TEXT}</span>
                    ) : (
                        filtered.map((user, i) => (
                            <span
                                key={user.userId}
                                role="option"
                                aria-selected={i === activeIndex}
                                className={clsx(styles.Row, i === activeIndex && styles.RowActive)}
                                // mousedown (not click) so the editor input doesn't blur-cancel
                                // the combobox before the selection lands.
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectUser(user);
                                }}
                                onMouseEnter={() => setActiveIndex(i)}
                            >
                                <span className={styles.Name}>{user.name || user.emailId}</span>
                                {user.name && user.emailId ? <span className={styles.Email}>{user.emailId}</span> : null}
                            </span>
                        ))
                    )}
                </span>,
                document.body,
            )}
            {props.children}
        </PlateElement>
    );
}
